/* eslint-disable max-classes-per-file */
import { createContext } from 'react'
import { CacheService, LOGGER } from '@cicero/data/utils'
import { RxCollection, RxDatabase, addRxPlugin, createRxDatabase } from 'rxdb'
import { Subscription, timer } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { Memoize } from 'typescript-memoize'
import { BaseSchema, CREATE_COLLECTION, IModel, PARSE_CONFIG_AS_SCHEMA  } from '../helpers'
import { ModelService } from './ModelService'
import { ApiClient } from './ApiClient'
import { UserSchema } from '../UserSchema'
import { UrlSchema } from '../UrlSchema'

/**
 * The store is a service that maintains a DB, event queue, and scheduler to sync
 * actions with one or more external systems such as an API
 */
export class Store {
  protected handlers = new Map<ActionHandlerConstructor, IActionHandler<any, any>>()
  protected dispatchSchedule = new Map<ActionHandlerConstructor, { when: number, sync: Subscription }>()
  API: ApiClient
  models = new Map<string, ModelService<any>>()
  cache = new CacheService()
  events: any[] = []
  session?: { user: UserSchema, url: UrlSchema }
  get stats() { return Array.from(this.models.values()).reduce((acc, m) => ({ ...acc, [m.schema.CONFIG.name_plural]: m.stats }), {}) }

  constructor(public config: IStoreConfig) {
    config.rxdbPlugins.forEach(plugin => addRxPlugin(plugin))
    this.getDB()
    this.API = new ApiClient(config.endpoint)

    config.actions.forEach(action => {
      this.handlers.set(action, new action())
    })
  }

  /**
   * Gets the DB instance, either from the cache or creates a new one. 
   * Stores a promise to bypass race conditions
   */
  @Memoize()
  async getDB(): Promise<RxDatabase> {
    const { name: dbName, rxdbAdapter, debug } = this.config
    const name = debug ? `${dbName}_${Date.now()}` : dbName
    return createRxDatabase({ name, adapter: rxdbAdapter })
  }

  /**
   * Gets the RxDB collection for a given model schema, either from the
   * cache or creates a new one
   * @param schema 
   */
  @Memoize((schema: IModel<any>) => schema.CONFIG.name_plural)
  async getCollection<T, M, CM>(schema: IModel<T>): Promise<RxCollection<T, M, CM>> {
    if (schema.LIFECYCLE.preInit) { schema = schema.LIFECYCLE.preInit(schema) }
    const rxSchema = PARSE_CONFIG_AS_SCHEMA(schema.CONFIG)
    const coll = await CREATE_COLLECTION(await this.getDB(), rxSchema)
    const { LIFECYCLE } = schema

    if (LIFECYCLE) {
      if (LIFECYCLE.onChange !== undefined) {
        coll.$
          .pipe(debounceTime(2000))
          .subscribe((c: any) => LIFECYCLE.onChange(this, c))
      }

      if (LIFECYCLE.preChange !== undefined) {
        coll.preSave(LIFECYCLE.preChange, true)
        coll.preInsert(LIFECYCLE.preChange, true)
      }

      if (LIFECYCLE.preSave !== undefined) { coll.preSave(LIFECYCLE.preSave, true) }
      if (LIFECYCLE.preInsert !== undefined) { coll.preInsert(LIFECYCLE.preInsert, true) }
    }

    return coll
  }

  /**
   * Gets a model, both its raw collection and some helper methods for
   * CRUD operations and other functions
   * @param schema 
   */
  @Memoize((schema: typeof BaseSchema) => schema.CONFIG.name_plural)
  getModel<T extends BaseSchema>(schema: (new () => T)) {
    const { CONFIG } = schema as any as IModel<T>
    const service = new ModelService(schema as any, this)
    this.models.set(CONFIG.name_plural, service)
    return service as any as ModelService<T>
  }

  /**
   * Returns a model just by its string name
   * @param name 
   */
  getModelByName(name: string) {
    const m = this.models.get(name)
    if (!m) { throw new Error(`Schema "${name}" has not been configured`) }
    return this.getModel(m.schema as any)
  }

  /**
   * Completely wipes the local DB, and then refreshes the page.
   */
  async wipe(refresh = true) {
    const db = await this.getDB()
    await db.remove()
    return this.wipeCache(refresh)
  }

  async wipeCache(refresh = true) {
    window.localStorage.clear()
    if (refresh) { window['location'].reload() }
    return true
  }

  async dump() {
    const db = await this.getDB()
    return db.dump()
  }

  /**
   * Dispatch an action based with the provided config
   * @param action 
   */
  async dispatch<T = any>(action: IAction<T>): Promise<IAction<T>|null> {
    const executor = this.handlers.get(action.type)
    if (!executor) {
      if (this.config.allowTestDispatch === true) {
        LOGGER.debug(`action "${action.type}" has no handler, but in config.allowTestDispatch is true to failing silently.`)
        return action
      }
      throw new Error(`There is no registered handler for action type "${action.type}". Please check your code for bugs.`)
    }

    try {
      if (action.beforeHook) { await action.beforeHook(this) }
      const result = await executor.exec(action, this)
      if (action.afterHook) { await action.afterHook(this) }
      if (action.afterSuccessHook) { await action.afterSuccessHook(this) }
      this.events.push(new DispatchResult(0, executor, result, action))
      LOGGER.debug('[DISPATCH]', executor, action, result)
      return result
    } catch (err) {
      if (action.afterHook) { await action.afterHook(this) }
      if (action.afterErrorHook) { await action.afterErrorHook(this) }
      this.events.push(new DispatchResult(500, executor, null, action, err))
      LOGGER.warn('[DISPATCH_ERR]', executor, { action, err })
      return null
    }
  }

  /**
   * Decide on the next selection of actions to dispatch and when to dispatch them
   * If an action of the same type is already scheduled, only schedule it if this one
   * must be run sooner
   * @param type the name of the action
   * @param maxWait the longest time in ms we can wait for this action
   * @todo improve maxwait config to schedule events at a certain time (e.g. delete all synced actions)
   */
  async schedule(type: ActionHandlerConstructor<any>, maxWait: number = this.config.defaultMaxWait) {
    const alreadyScheduled = this.dispatchSchedule.get(type)
    if (!alreadyScheduled || alreadyScheduled.when > Date.now() + (maxWait || Infinity)) {
      const delay = timer(maxWait).subscribe(async () => {
        await this.dispatch({ type })
        this.dispatchSchedule.delete(type)
      })
      this.dispatchSchedule.set(type, { when: Date.now() + maxWait, sync: delay })
    } else {
      // LOGGER.debug('[ALREADY_SCHEDULED]', type)
    }
  }

  getSetting(name: string, defaultValue: string): string {
    const valIfExists: string|null = window.localStorage.getItem(`setting.${name}`)
    return valIfExists === null ? defaultValue : valIfExists
  }

  setSetting(name: string, value: string) {
    window.localStorage.setItem(`setting.${name}`, value)
  }
}

export class DispatchResult {
  constructor(public code: number, public action: any, public result: any, public config: any, public err?: any) {}
}

export const StoreContext = createContext<Store>(undefined as any)
export type ActionHandlerConstructor<P = any, R = any> = { new(): IActionHandler<P, R>, name: string }
export type ActionHandlerFunc<T = any> = (action: IAction<T>, store: any) => Promise<IAction<T>>

export interface IStoreConfig {
  debug?: boolean
  defaultMaxWait: number
  endpoint: string
  name: string
  rxdbAdapter: 'idb'|'memory'
  rxdbPlugins: any[]
  allowTestDispatch?: boolean // Actions triggered that have no registered handler will fail softly if true. Used for testing.
  actions: ActionHandlerConstructor<any, any>[]
}

export interface IAction<T = any> {
  id?: number
  type: ActionHandlerConstructor
  beforeHook?: (store: Store) => any
  afterSuccessHook?: (store: Store) => any
  afterErrorHook?: (store: Store) => any
  afterHook?: (store: Store) => any
  payload?: T
  response?: {
    code: number
    payload: T
  }
}

export interface IActionHandlerSchema<T> extends IActionConfig {
  handler: ActionHandlerFunc<T>
}

export interface IActionHandler<PayloadSchema, ResponseSchema> {
  stats?: IActionHandlerStats
  exec(action: IAction<PayloadSchema>, store: any): ResponseSchema
}

export interface IActionHandlerStats {
  runs: number
  failures: number
}

export interface IActionConfig {
  delay?: number|Date
}

